import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Modal, Select, Input, DatePicker } from 'antd';
import dayjs from 'dayjs';
import type { ColumnType } from 'antd/es/table';
import { LeftOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import CsvExcelImport from '../../../components/common/CsvExcelImport';
import { useGetMatLogisticsMppl } from '../../../hooks/genericHooks';
import type { MatLogisticsMppl } from '../../../types';
import Button from '../../../components/common/Button';

const MatLogMpplList: React.FC = () => {
  const navigate = useNavigate();
  const { data = [], isLoading, refetch } = useGetMatLogisticsMppl() as any;
  const dataSource: MatLogisticsMppl[] = Array.isArray(data) ? data : (data?.data ?? []);
  const [importOpen, setImportOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<{
    area?: string;
    del_terms?: string;
    planned_shipment_month?: number | null;
    country_of_shipping?: string;
    port_of_loading?: string;
    destination?: string;
    po_no?: string[];
    pkg_text?: string; // matches po_package or order_package
    desc_text?: string; // matches description or sub_description
    cargo_category?: string;
    date_from?: string; // YYYY-MM-DD
    date_to?: string;   // YYYY-MM-DD
    group_name?: string[];
    serial_no_text?: string;
  }>({});

  const applyFilters = (rows: MatLogisticsMppl[], f = filters) => {
    return rows.filter((r) => {
      if (f.area && r.area !== f.area) return false;
      if (f.del_terms && r.del_terms !== f.del_terms) return false;
      if (
        f.planned_shipment_month != null &&
        r.planned_shipment_month !== f.planned_shipment_month
      )
        return false;
      if (f.country_of_shipping && r.country_of_shipping !== f.country_of_shipping) return false;
      if (f.port_of_loading && r.port_of_loading !== f.port_of_loading) return false;
      if (f.destination && r.destination !== f.destination) return false;
      if (Array.isArray(f.group_name) && f.group_name.length > 0) {
        if (!f.group_name.includes(r.group_name as any)) return false;
      }
      if (f.serial_no_text && String(f.serial_no_text).trim() !== '') {
        const q = String(f.serial_no_text).trim();
        const n = Number(q);
        if (!Number.isNaN(n)) {
          if ((r.serial_no as any) !== n) return false;
        } else {
          if (!String(r.serial_no ?? '').includes(q)) return false;
        }
      }
      // Date range filter (inclusive) on planned_shipment_date
      if (f.date_from || f.date_to) {
        const d = r.planned_shipment_date ? String(r.planned_shipment_date).slice(0, 10) : null;
        if (!d) return false;
        if (f.date_from && d < f.date_from) return false;
        if (f.date_to && d > f.date_to) return false;
      }
      // Text contains filters (case-insensitive)
      const ci = (v?: any) => (v === null || v === undefined ? '' : String(v)).toLowerCase();
      if (Array.isArray(f.po_no) && f.po_no.length > 0) {
        if (!f.po_no.includes(r.po_no as any)) return false;
      }
      if (f.pkg_text) {
        const q = String(f.pkg_text).toLowerCase().trim();
        if (!ci(r.po_package).includes(q) && !ci(r.order_package).includes(q)) return false;
      }
      if (f.desc_text) {
        const q = String(f.desc_text).toLowerCase().trim();
        if (!ci(r.description).includes(q) && !ci(r.sub_description).includes(q)) return false;
      }
      if (f.cargo_category && r.cargo_category !== f.cargo_category) return false;
      return true;
    });
  };

  const filteredData = useMemo(() => applyFilters(dataSource), [dataSource, filters]);

  const makeOptions = <K extends keyof MatLogisticsMppl>(
    key: K,
    excludeKey?: keyof typeof filters
  ) => {
    const base = excludeKey
      ? applyFilters(dataSource, { ...filters, [excludeKey]: undefined } as any)
      : applyFilters(dataSource);
    const set = new Set(
      base
        .map((r) => r[key])
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '') as any
    );
    return Array.from(set).map((v: any) => ({ label: String(v), value: v }));
  };

  const columns: ColumnType<MatLogisticsMppl>[] = useMemo(
    () => [
      { title: 'Serial No', dataIndex: 'serial_no', key: 'serial_no', width: 80, sorter: (a, b) => (a.serial_no ?? 0) - (b.serial_no ?? 0) },
      { title: 'Group', dataIndex: 'group_name', key: 'group_name', width: 65 },
      { title: 'PO No', dataIndex: 'po_no', key: 'po_no', width: 100 },
      { title: 'PO Package', dataIndex: 'po_package', key: 'po_package', width: 120 },
      { title: 'Order Package', dataIndex: 'order_package', key: 'order_package', width: 120 },
      { title: 'Item Tag', dataIndex: 'item_tag', key: 'item_tag', width: 120 },
      { title: 'Description', dataIndex: 'description', key: 'description', width: 220 },
      { title: 'Sub Description', dataIndex: 'sub_description', key: 'sub_description', width: 180 },
      { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', width: 160   },
      { title: 'Qty', dataIndex: 'planned_qty', key: 'planned_qty', width: 90, align: 'right' },
      { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
      {
        title: 'Planned Date',
        dataIndex: 'planned_shipment_date',
        key: 'planned_shipment_date',
        width: 110,
        render: (v) => (v ? String(v).slice(0, 10) : '-'),
      },
      { title: 'Del Terms', dataIndex: 'del_terms', key: 'del_terms', width: 100 },
      { title: 'Area', dataIndex: 'area', key: 'area', width: 80 },
      { title: 'Country of Shipping', dataIndex: 'country_of_shipping', key: 'country_of_shipping', width: 150 },
      { title: 'Port of Loading', dataIndex: 'port_of_loading', key: 'port_of_loading', width: 140 },
      { title: 'Destination', dataIndex: 'destination', key: 'destination', width: 140 },
      { title: 'Cargo Category', dataIndex: 'cargo_category', key: 'cargo_category', width: 140 },
      { title: 'Shipment Month', dataIndex: 'planned_shipment_month', key: 'planned_shipment_month', width: 120 },
    ],
    []
  );

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} />
            <Typography.Title level={4} style={{ margin: 0 }}>
              MPPL List {filteredData?.length ? `(${filteredData.length})` : ''}
            </Typography.Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button action="add" icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              Import CSV/Excel
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch?.()}>
              Refresh
            </Button>
            <Button onClick={() => setFilters({})}>Clear Filters</Button>
            <Button onClick={() => setShowFilters((v) => !v)}>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Space>
        </Col>
      </Row>
      {showFilters && (
      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
        <Col>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="PO No"
            style={{ width: 180 }}
            value={filters.po_no as any}
            options={makeOptions('po_no', 'po_no')}
            onChange={(v) => setFilters((p) => ({ ...p, po_no: (v && v.length ? v : undefined) as any }))}
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Group"
            style={{ width: 100 }}
            value={filters.group_name as any}
            options={makeOptions('group_name', 'group_name')}
            onChange={(v) => setFilters((p) => ({ ...p, group_name: (v && v.length ? v : undefined) as any }))}
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Input
            allowClear
            placeholder="Serial No"
            style={{ width: 100 }}
            value={filters.serial_no_text}
            onChange={(e) => setFilters((p) => ({ ...p, serial_no_text: e.target.value || undefined }))}
          />
        </Col>
        <Col>
          <Input
            allowClear
            placeholder="Search PO/Order Package"
            style={{ width: 200 }}
            value={filters.pkg_text}
            onChange={(e) => setFilters((p) => ({ ...p, pkg_text: e.target.value || undefined }))}
          />
        </Col>
        <Col>
          <Input
            allowClear
            placeholder="Search Description/Sub"
            style={{ width: 220 }}
            value={filters.desc_text}
            onChange={(e) => setFilters((p) => ({ ...p, desc_text: e.target.value || undefined }))}
          />
        </Col>
        <Col>
          <DatePicker.RangePicker
            allowClear
            placeholder={["From", "To"]}
            style={{ width: 220 }}
            value={
              (filters.date_from || filters.date_to)
                ? [filters.date_from ? dayjs(filters.date_from) : null, filters.date_to ? dayjs(filters.date_to) : null] as any
                : undefined
            }
            onChange={(vals) =>
              setFilters((p) => ({
                ...p,
                date_from: vals?.[0] ? vals[0].format('YYYY-MM-DD') : undefined,
                date_to: vals?.[1] ? vals[1].format('YYYY-MM-DD') : undefined,
              }))
            }
          />
        </Col>
      </Row>
      )}
      {showFilters && (
      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
        <Col>
          <Select
            allowClear
            showSearch
            placeholder="Area"
            style={{ width: 80 }}
            value={filters.area}
            options={makeOptions('area', 'area')}
            onChange={(v) => setFilters((p) => ({ ...p, area: v || undefined }))}
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Select
            allowClear
            showSearch
            placeholder="Terms"
            style={{ width: 80 }}
            value={filters.del_terms}
            options={makeOptions('del_terms', 'del_terms')}
            onChange={(v) => setFilters((p) => ({ ...p, del_terms: v || undefined }))}
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Select
            allowClear
            showSearch
            placeholder="Country of Shipping"
            style={{ width: 100 }}
            value={filters.country_of_shipping}
            options={makeOptions('country_of_shipping', 'country_of_shipping')}
            onChange={(v) => setFilters((p) => ({ ...p, country_of_shipping: v || undefined }))}
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Select
            allowClear
            showSearch
            placeholder="Port of Loading"
            style={{ width: 160 }}
            value={filters.port_of_loading}
            options={makeOptions('port_of_loading', 'port_of_loading')}
            onChange={(v) => setFilters((p) => ({ ...p, port_of_loading: v || undefined }))}
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Select
            allowClear
            showSearch
            placeholder="Destination"
            style={{ width: 160 }}
            value={filters.destination}
            options={makeOptions('destination', 'destination')}
            onChange={(v) => setFilters((p) => ({ ...p, destination: v || undefined }))}
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Select
            allowClear
            showSearch
            placeholder="Cargo Category"
            style={{ width: 160 }}
            value={filters.cargo_category}
            options={makeOptions('cargo_category', 'cargo_category')}
            onChange={(v) => setFilters((p) => ({ ...p, cargo_category: v || undefined }))}
            optionFilterProp="label"
          />
        </Col>
      </Row>
      )}
      <StandardTable<MatLogisticsMppl>
        columns={columns}
        dataSource={filteredData}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        enableCellTooltips
        scroll={{ x: 'max-content' }}
      />
      <Modal
        open={importOpen}
        title="Import MPPL from CSV/Excel"
        onCancel={() => setImportOpen(false)}
        footer={null}
        destroyOnClose
      >
        <CsvExcelImport
          tableName="mat_logistics_mppl"
          onSuccess={() => {
            refetch?.();
            setImportOpen(false);
          }}
          onError={() => {}}
        />
      </Modal>
    </Card>
  );
};

export default MatLogMpplList;
